/**
 * Plan-Based Feature Configuration (Frontend)
 *
 * This mirrors the backend configuration and provides
 * feature definitions for UI display.
 */

import {
  FileSignature,
  LayoutTemplate,
  Send,
  Link,
  UserCheck,
  ListOrdered,
  Library,
  Folder,
  Tag,
  Palette,
  EyeOff,
  Mail,
  Code,
  Webhook,
  Code2,
  FileCode,
  BarChart2,
  LineChart,
  Download,
  Shield,
  Lock,
  Users,
  UserCog,
  Headphones,
  UserCircle,
} from "lucide-react";

/**
 * Feature definitions with metadata for UI display
 */
export const FEATURE_DEFINITIONS = {
  // Core signing features
  basicSigning: {
    name: "Basic Signing",
    description: "Send documents for signature",
    category: "signing",
    icon: FileSignature,
  },
  templates: {
    name: "Templates",
    description: "Create reusable document templates",
    category: "signing",
    icon: LayoutTemplate,
  },
  bulkSend: {
    name: "Bulk Send",
    description: "Send documents to multiple recipients at once",
    category: "signing",
    icon: Send,
  },
  signingLinks: {
    name: "Signing Links",
    description: "Create shareable links for document signing",
    category: "signing",
    icon: Link,
  },
  inPersonSigning: {
    name: "In-Person Signing",
    description: "Facilitate face-to-face signing sessions",
    category: "signing",
    icon: UserCheck,
  },
  sequentialSigning: {
    name: "Sequential Signing",
    description: "Define signing order for multiple recipients",
    category: "signing",
    icon: ListOrdered,
  },

  // Organisation features
  clauseLibrary: {
    name: "Clause Library",
    description: "Store and reuse common document clauses",
    category: "organisation",
    icon: Library,
  },
  folders: {
    name: "Folders",
    description: "Organize documents into folders",
    category: "organisation",
    icon: Folder,
  },
  tags: {
    name: "Tags",
    description: "Tag and categorize documents",
    category: "organisation",
    icon: Tag,
  },

  // Branding & customization
  customBranding: {
    name: "Custom Branding",
    description: "Add your logo and colors to signing experience",
    category: "branding",
    icon: Palette,
  },
  whiteLabel: {
    name: "White Label",
    description: "Remove eScribe branding completely",
    category: "branding",
    icon: EyeOff,
  },
  customEmails: {
    name: "Custom Email Templates",
    description: "Customize notification emails",
    category: "branding",
    icon: Mail,
  },

  // Developer & integration
  apiAccess: {
    name: "API Access",
    description: "Access eScribe via REST API",
    category: "developer",
    icon: Code,
  },
  webhooks: {
    name: "Webhooks",
    description: "Receive real-time event notifications",
    category: "developer",
    icon: Webhook,
  },
  embedSdk: {
    name: "Embed SDK",
    description: "Embed signing in your application",
    category: "developer",
    icon: Code2,
  },
  customCss: {
    name: "Custom CSS",
    description: "Apply custom styles to embedded interface",
    category: "developer",
    icon: FileCode,
  },

  // Analytics & reporting
  basicAnalytics: {
    name: "Basic Analytics",
    description: "View signing activity and status",
    category: "analytics",
    icon: BarChart2,
  },
  advancedAnalytics: {
    name: "Advanced Analytics",
    description: "Detailed reports and insights",
    category: "analytics",
    icon: LineChart,
  },
  auditExports: {
    name: "Audit Trail Exports",
    description: "Export detailed audit logs",
    category: "analytics",
    icon: Download,
  },

  // Security & compliance
  sso: {
    name: "SSO / SAML",
    description: "Single sign-on integration",
    category: "security",
    icon: Shield,
  },
  advancedSecurity: {
    name: "Advanced Security",
    description: "IP restrictions, session controls",
    category: "security",
    icon: Lock,
  },
  signerVerification: {
    name: "Signer Verification",
    description: "SMS, email, and ID verification",
    category: "security",
    icon: UserCheck,
  },

  // Team features
  teamMembers: {
    name: "Team Members",
    description: "Add team members to your organisation",
    category: "team",
    icon: Users,
  },
  roleManagement: {
    name: "Role Management",
    description: "Assign roles and permissions",
    category: "team",
    icon: UserCog,
  },

  // Support
  prioritySupport: {
    name: "Priority Support",
    description: "Faster response times",
    category: "support",
    icon: Headphones,
  },
  dedicatedManager: {
    name: "Dedicated Account Manager",
    description: "Personal support contact",
    category: "support",
    icon: UserCircle,
  },
};

/**
 * Feature categories for UI grouping
 */
export const FEATURE_CATEGORIES = {
  signing: { name: "Signing", order: 1 },
  organisation: { name: "Organization", order: 2 },
  branding: { name: "Branding & Customization", order: 3 },
  developer: { name: "Developer & Integrations", order: 4 },
  analytics: { name: "Analytics & Reporting", order: 5 },
  security: { name: "Security & Compliance", order: 6 },
  team: { name: "Team Management", order: 7 },
  support: { name: "Support", order: 8 },
};

/**
 * Plan display info
 */
export const PLAN_INFO = {
  free: {
    id: "free",
    name: "Free",
    description: "For individuals getting started",
    price: 0,
    color: "gray",
  },
  starter: {
    id: "starter",
    name: "Starter",
    description: "For small teams and growing businesses",
    price: 19,
    color: "blue",
  },
  professional: {
    id: "professional",
    name: "Professional",
    description: "For teams that need advanced features",
    price: 39,
    color: "purple",
    popular: true,
  },
  enterprise: {
    id: "enterprise",
    name: "Enterprise",
    description: "For large organisations with custom needs",
    price: null,
    color: "amber",
  },
};

/**
 * Map features to sidebar/page routes
 * Used to determine which nav items should show upgrade badge
 */
export const FEATURE_ROUTES = {
  bulkSend: "/bulk-send",
  analytics: "/analytics",
  advancedAnalytics: "/analytics",
  apiAccess: "/api-keys",
  webhooks: "/webhooks",
  embedSdk: "/embed",
  inPersonSigning: "/in-person",
  clauseLibrary: "/clause-library",
  sso: "/settings/sso",
  customBranding: "/settings/branding",
};

/**
 * Reverse mapping: route to required feature
 */
export const ROUTE_FEATURES = {
  "/bulk-send": "bulkSend",
  "/analytics": "advancedAnalytics",
  "/api-keys": "apiAccess",
  "/webhooks": "webhooks",
  "/embed": "embedSdk",
  "/in-person": "inPersonSigning",
  "/clause-library": "clauseLibrary",
  "/settings/sso": "sso",
  "/settings/branding": "customBranding",
};

/**
 * Get feature info by key
 */
export function getFeatureInfo(featureKey) {
  return FEATURE_DEFINITIONS[featureKey] || null;
}

/**
 * Get plan info by ID
 */
export function getPlanInfo(planId) {
  return PLAN_INFO[planId] || PLAN_INFO.free;
}

/**
 * Get minimum plan name for upgrade prompt
 */
export function getMinimumPlanForFeature(featureKey, _features = {}) {
  // This would ideally come from the backend
  // For now, we'll use a simple mapping
  const featurePlanMap = {
    bulkSend: "professional",
    inPersonSigning: "starter",
    clauseLibrary: "starter",
    apiAccess: "professional",
    webhooks: "professional",
    embedSdk: "professional",
    customBranding: "starter",
    whiteLabel: "professional",
    advancedAnalytics: "starter",
    auditExports: "professional",
    sso: "enterprise",
    teamMembers: "starter",
    roleManagement: "professional",
  };

  return featurePlanMap[featureKey] || "starter";
}

export default {
  FEATURE_DEFINITIONS,
  FEATURE_CATEGORIES,
  PLAN_INFO,
  FEATURE_ROUTES,
  ROUTE_FEATURES,
  getFeatureInfo,
  getPlanInfo,
  getMinimumPlanForFeature,
};
